import React from "react";
import {
  LabelEntityType,
  useGetLabelsQuery,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { Select } from "@src/common/components";
import { SelectOverrides } from "baseui/select";

interface LabelsFilterProps {
  entityType: LabelEntityType;
  selectedLabelIds?: string[];
  onLabelChange: (labelIds: string[]) => void;
  placeholder?: string;
  size?: "mini" | "large" | "default" | "compact";
  disabled?: boolean;
  selectOverrides?: SelectOverrides;
}

export function LabelsFilter({
  entityType,
  selectedLabelIds = [],
  onLabelChange,
  placeholder = "Select Labels",
  size = "mini",
  disabled = false,
  selectOverrides,
}: LabelsFilterProps) {
  const { courier } = useAuth();
  const courierId = courier?.courier?.id;
  const showError = useErrorToast();

  // Fetch available labels
  const {
    data: labelsData,
    loading: labelsLoading,
    error: labelsError,
  } = useGetLabelsQuery({
    variables: {
      courierId: courierId || "",
      entityType,
    },
    skip: !courierId,
    onError: (error) => {
      showError(error);
    },
  });

  const labels = labelsData?.labels || [];

  // Create label options for Select component
  const labelOptions = labelsData?.labels
    ? labels.map((label) => ({ id: label.id, label: label.name }))
    : [];

  const handleLabelChange = (params: any) => {
    const selectedIds = params.value.map((v: { id: string }) => v.id);
    onLabelChange(selectedIds);
  };

  // Handle value calculation with undefined data protection
  const value = labelOptions.filter((option) => selectedLabelIds.includes(option.id));

  return (
    <Select
      size={size}
      multi
      options={labelOptions}
      value={value}
      placeholder={
        labelsLoading
          ? "Loading labels..."
          : labelOptions.length === 0
          ? "No labels available"
          : placeholder
      }
      clearable={true}
      searchable={true}
      onChange={handleLabelChange}
      isLoading={labelsLoading}
      disabled={disabled || labelsLoading || labelOptions.length === 0}
      overrides={selectOverrides}
    />
  );
}
